import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import ContentLoader from 'react-content-loader';
import { icons } from '../generic/Icon';
import SubscriptionStore from '../../redux/subscriptions';
import { useDispatchOnMount } from '../../hooks';
import COLORS from '../../theme/colors';
import type { BillingTermUsageResponse } from '../../../typings/api';
import Badge, { AlertColors, BadgeSizes } from '../generic/Badge/Badge';
import TooltipBadge from '../generic/Badge/TooltipBadge';

/**
 * Gets badge type for revision usage
 * @param usageInfo {BillingTermUsageResponse} Usage info for org
 * @returns {AlertKind} Alert type to color badge
 */
const getRevisionUsageColor = (usageInfo?: BillingTermUsageResponse | null) => {
  if (!usageInfo) {
    return AlertColors.Error;
  }

  const percentUsage = Math.round((usageInfo.usage / usageInfo.quantity) * 100);
  if (percentUsage >= 90) {
    return AlertColors.Error;
  }

  if (percentUsage >= 80) {
    return AlertColors.Warning;
  }

  return AlertColors.Info;
};

/**
 * Loader for revision usage badge
 */
const Loader: VoidFunctionComponent = () => (
  <ContentLoader
    width="140px"
    height="40px"
    viewBox="0 0 140 12"
    backgroundColor={COLORS.fossaGray60}
    foregroundColor={COLORS.fossaGray40}
    style={{ marginTop: '5px' }}
  >
    <rect x="0" y="0" rx="3" ry="3" width="100%" height="12" />
  </ContentLoader>
);

/**
 * Badge that shows/alerts how many revisions are left for a per revision pricing plan
 */
const RevisionUsageBadge: VoidFunctionComponent = () => {
  const subscription = useSelector(SubscriptionStore.selectors.getSubscription);
  const { isLoading } = useSelector(SubscriptionStore.selectors.getLoading);

  useDispatchOnMount(SubscriptionStore.actions.fetchSubscription);

  if (isLoading) {
    return (
      <Badge
        color={AlertColors.Info}
        size={BadgeSizes.Large}
      >
        <Loader />
      </Badge>
    );
  }

  const { usage, quantity: limit } = subscription?.usage || { usage: 0, quantity: 0 };
  const remaining = Math.max(limit - usage, 0);

  return (
    <TooltipBadge
      id="revision-based-pricing-badge"
      icon={icons.infoCircle}
      color={getRevisionUsageColor(subscription?.usage)}
      size={BadgeSizes.Large}
      tooltip={
        <span>
          Your organization is subscribed to our <b>Revision-based Pricing</b>.{' ' /* a space wasn't being rendered here */}
          <b>Contact Sales</b> or visit your <b>Billing page</b> to learn more about this.
        </span>
      }
      placement="bottom"
    >
      <b>{remaining}</b> of <b>{limit}</b> revisions left
    </TooltipBadge>

  );
};

export default RevisionUsageBadge;
